import { ImageCropper as AImageCropper } from '@ark-ui/solid/image-cropper';
import { For, splitProps } from 'solid-js';
import { tv } from 'tailwind-variants';
import type { ComponentProps } from 'solid-js';
import type { VariantProps } from 'tailwind-variants';

type Props = ComponentProps<typeof AImageCropper.RootProvider> &
	VariantProps<typeof styles> & { src: string; alt?: string };

export function ImageCropper(props: Props) {
	const [, spread] = splitProps(props, ['src', 'alt', 'aspectRatio']);
	return (
		<AImageCropper.RootProvider {...spread}>
			<AImageCropper.Viewport class={styles({ aspectRatio: props.aspectRatio }).viewport()}>
				<AImageCropper.Image
					src={props.src}
					alt={props.alt ?? ''}
					// NOTE: Ark adds `object-fit: fill` to the style attribute, which messes up the layout.
					// `object-contain` must be marked important to override this
					class="absolute h-full object-contain!"
				/>

				<AImageCropper.Selection
					// oxlint-disable-next-line tailwindcss/no-hardcoded-colors
					class="cursor-move border-2 border-white/50 shadow-[0_0_0_9999px_rgba(0,0,0,0.5)] data-[shape=circle]:rounded-full"
				>
					<For each={AImageCropper.handles}>
						{(position) => (
							<AImageCropper.Handle position={position} class="absolute grid size-2 place-items-center">
								<div class="size-2 border border-zinc-50 bg-zinc-950 transition-all" />
							</AImageCropper.Handle>
						)}
					</For>
					<AImageCropper.Grid
						axis="horizontal"
						class="pointer-events-none absolute inset-y-1/3 border-y border-white/50 opacity-0 transition-opacity data-dragging:opacity-100 data-panning:opacity-100"
					/>
					<AImageCropper.Grid
						axis="vertical"
						class="pointer-events-none absolute inset-x-1/3 border-x border-white/50 opacity-0 transition-opacity data-dragging:opacity-100 data-panning:opacity-100"
					/>
				</AImageCropper.Selection>
			</AImageCropper.Viewport>
		</AImageCropper.RootProvider>
	);
}

const styles = tv({
	slots: {
		viewport: 'relative grid place-items-center overflow-hidden bg-zinc-300 dark:bg-zinc-700',
	},
	variants: {
		aspectRatio: {
			square: { viewport: 'aspect-square' },
			video: { viewport: 'aspect-video' },
		},
	},
	defaultVariants: {
		aspectRatio: 'video',
	},
});

export { useImageCropper } from '@ark-ui/solid/image-cropper';
